"use client"

import { useState } from "react"
import axios from "axios"
import moment from "moment"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  styled,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
}))

export default function NewBonusCardDialog({ provider, open, onClose, branches, onSave }) {
  const [formData, setFormData] = useState({
    status: "enabled",
    maxPoints: 0,
    description: "",
    validUntil: null,
    cardImage: null,
    branchId: "", // 👈 added
    qrCodes: [{ status: "active", points: 5, expiryDate: null }],
  })

  const [imageFile, setImageFile] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      setImageFile(e.target.files[0])
      setFormData((prev) => ({
        ...prev,
        cardImage: e.target.files[0],
      }))
    }
  }

  const onPressQcode = async (e) => {
    e.preventDefault()
    if (!imageFile) {
      setSnackbar({ open: true, message: "Please select an image file.", severity: "error" })
      return
    }

    setFetching(true)

    try {
      // Step 1: Upload the Image
      const imageFormData = new FormData()
      imageFormData.append("file", imageFile)

      const imageUploadResponse = await axios.post(`${process.env.REACT_APP_API_URL}/uploadFile`, imageFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      const imageUrl = imageUploadResponse.data.url

      // Step 2: Create Card with the Image URL
      const formattedDate = moment(formData.validUntil).unix()
      const data = {
        vendorId: provider._id,
        branchId: formData.branchId, // 👈 include branch
        status: formData.status,
        maxPoints: formData.maxPoints,
        validUntil: formattedDate,
        qrCodes: formData.qrCodes,
        details: formData.description,
        image: imageUrl,
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/bonus_cards/add_card`, data)

      setSnackbar({ open: true, message: "Card created successfully!", severity: "success" })
      setFormData({
        status: "enabled",
        maxPoints: 0,
        description: "",
        validUntil: null,
        cardImage: null,
        branchId: "",
        qrCodes: [{ status: "active", points: 5, expiryDate: null }],
      })
      onSave(data)
      onClose()
    } catch (error) {
      console.error("Error:", error)
      setSnackbar({ open: true, message: "Error creating card. Please try again.", severity: "error" })
    } finally {
      setFetching(false)
    }
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">New Bonus Card</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={onPressQcode}>
        <DialogContent>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <RadioGroup row name="status" value={formData.status} onChange={handleChange}>
                <FormControlLabel value="enabled" control={<Radio />} label="Enabled" />
                <FormControlLabel value="disabled" control={<Radio />} label="Disabled" />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>Branch</FormLabel>
              <Select
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Branch
                </MenuItem>
                {branches?.map((branch) => (
                  <MenuItem key={branch._id} value={branch._id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Max Points"
              name="maxPoints"
              type="number"
              value={formData.maxPoints}
              onChange={handleChange}
              fullWidth
              required
            />

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
            />

            <DatePicker
              label="Valid Until"
              value={formData.validUntil}
              onChange={(newValue) => setFormData((prev) => ({ ...prev, validUntil: newValue }))}
              slotProps={{ textField: { fullWidth: true } }}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Card Image
              </Typography>
              <Button variant="outlined" component="label">
                Choose File
                <input type="file" hidden onChange={handleFileChange} accept="image/*" />
              </Button>
              {formData.cardImage && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected file: {formData.cardImage.name}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button type="submit" variant="contained" color="primary" disabled={fetching}>
            {fetching ? <CircularProgress size={24} /> : "Create Card"}
          </Button>
        </DialogActions>
      </form>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledDialog>
  )
}
